"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useInView, useReducedMotion } from "framer-motion";
import { stegaClean } from "@sanity/client/stega";
import type { AiMedia } from "@/sanity/pages/PageAiFeaturesTemplate";
import { ProductImage } from "./ui";
import styles from "./ai-features.module.scss";

const SOURCE = "/ai-features/hero-trading-vector.svg";
const MOBILE = "/ai-features/hero-trading-3x.webp";
// Match asset identity, not filenames, to avoid replacing custom CMS artwork.
function isSource(
  media: AiMedia | undefined,
  local: string[],
  assets: string[],
) {
  return (
    (!!media?.src && local.includes(media.src)) ||
    (media?.src?.startsWith("https://cdn.sanity.io/images/") &&
      assets.some((asset) => media.src.split("?")[0].endsWith("/" + asset)))
  );
}

function animatedArtwork(source: string) {
  const svg = new DOMParser().parseFromString(source, "image/svg+xml");
  const screen = svg.querySelector('[clip-path="url(#clip1_3055_100575)"]');
  if (!screen || screen.children.length !== 111)
    throw new Error("Hero artwork no longer matches its source layers");
  const layers = [...screen.children];
  // These layer indices depend on the bundled Figma export and its draw order.
  const parts = [
    { name: "question", indices: [24, 25, 26, 27], at: 120, duration: 450 },
    { name: "coach", indices: [48], at: 520, duration: 360 },
    { name: "answer-intro", indices: [49], at: 900, duration: 1250 },
    { name: "answer-detail", indices: [50], at: 1700, duration: 1500 },
    {
      name: "answer-actions",
      indices: [51, 52, 53, 54],
      at: 3150,
      duration: 320,
    },
    {
      name: "next-question",
      indices: [28, 29, 30, 31],
      at: 3750,
      duration: 450,
    },
    {
      name: "thinking",
      indices: [40, 41, 42, 43, 44, 45, 46, 47],
      at: 4250,
      duration: 350,
    },
    { name: "strategy", indices: [55], at: 4650, duration: 400 },
    { name: "research", indices: [56], at: 5000, duration: 400 },
    { name: "data", indices: [57], at: 5350, duration: 450 },
  ];
  for (const part of parts) {
    const group = svg.createElementNS("http://www.w3.org/2000/svg", "g");
    group.setAttribute("data-hero-part", part.name);
    group.setAttribute("data-hero-at", String(part.at));
    group.setAttribute("data-hero-duration", String(part.duration));
    screen.insertBefore(group, layers[part.indices[0]]);
    part.indices.forEach((index) => group.append(layers[index]));
  }
  svg.documentElement.setAttribute("aria-hidden", "true");
  return new XMLSerializer().serializeToString(svg.documentElement);
}

export default function HeroArtwork({
  image,
  mobileImage,
}: {
  image?: AiMedia;
  mobileImage?: AiMedia;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [markup, setMarkup] = useState("");
  const [failed, setFailed] = useState(false);
  const seen = useInView(root, { amount: 0.05, once: true, margin: "200px" });
  const reducedMotion = useReducedMotion();
  const desktop = stegaClean(image);
  const mobile = stegaClean(mobileImage);
  const cropped = (media?: AiMedia) =>
    media?.crop && Object.values(media.crop).some((amount) => amount !== 0);
  const original =
    isSource(
      desktop,
      [SOURCE, "/ai-features/hero-vector.svg"],
      [
        "dff7886ddc3f8ebaabd782970f62d07eeaa4e521-1792x1137.svg",
        "7466088441da0ae674c2f6c317ab5462331c3dde-1792x1137.svg",
      ],
    ) &&
    !cropped(desktop) &&
    !cropped(mobile) &&
    (!mobile?.src ||
      isSource(
        mobile,
        [
          MOBILE,
          "/ai-features/hero-trading-2x.webp",
          "/ai-features/hero-2x.webp",
        ],
        [
          "0cf88aee4c35c56aac2a7aab6a7dba2aa03370fd-5376x3411.webp",
          "56a5666c7d855f4ed29f4ddf3398d13782cf9545-3584x2274.webp",
          "7fa3251cd48cf7866037e258db7553a8bdfdd507-3584x2274.webp",
        ],
      ));

  useEffect(() => {
    if (!original || !seen) return;
    const controller = new AbortController();
    // Only this bundled SVG is inlined; CMS images keep their normal pipeline.
    setFailed(false);
    fetch(SOURCE, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error("Hero artwork unavailable");
        return response.text();
      })
      .then((source) => {
        if (!controller.signal.aborted) setMarkup(animatedArtwork(source));
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });
    return () => controller.abort();
  }, [original, seen]);

  useEffect(() => {
    const node = root.current;
    if (!node || !markup || !original) return;
    node.dataset.heroState = "complete";
    if (reducedMotion) return;
    const animations = [
      ...node.querySelectorAll<SVGGElement>("[data-hero-part]"),
    ].map((part) => {
      const answer =
        part.dataset.heroPart === "answer-intro" ||
        part.dataset.heroPart === "answer-detail";
      const hidden: Keyframe = answer
        ? { opacity: 0, clipPath: "inset(0 0 100% 0) fill-box" }
        : { opacity: 0, transform: "translateY(8px)" };
      const shown: Keyframe = answer
        ? { opacity: 1, clipPath: "inset(0 0 0 0) fill-box" }
        : { opacity: 1, transform: "translateY(0)" };
      const cycle = 9200;
      const at = Number(part.dataset.heroAt);
      const duration = Number(part.dataset.heroDuration);
      const animation = part.animate(
        [
          { ...hidden, offset: 0 },
          {
            ...hidden,
            offset: at / cycle,
            easing: "cubic-bezier(0.16, 1, 0.3, 1)",
          },
          { ...shown, offset: (at + duration) / cycle },
          { ...shown, offset: 8800 / cycle },
          { ...shown, opacity: 0, offset: 1 },
        ],
        { duration: cycle, iterations: Infinity, fill: "both" },
      );
      animation.pause();
      return animation;
    });
    let inView = false;
    const sync = () => {
      const awake = inView && !document.hidden;
      animations.forEach((animation) => {
        if (awake) animation.play();
        else animation.pause();
      });
      node.dataset.heroState = awake ? "playing" : "paused";
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        sync();
      },
      { threshold: 0.15 },
    );
    observer.observe(node);
    document.addEventListener("visibilitychange", sync);
    sync();
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", sync);
      animations.forEach((animation) => animation.cancel());
    };
  }, [markup, original, reducedMotion]);

  // Headline rotation must preserve the SVG groups held by the native animations.
  const vectorMarkup = useMemo(() => ({ __html: markup }), [markup]);
  const ready = original && !!markup;
  return (
    <div
      ref={root}
      className={styles.heroArtwork}
      data-hero-artwork
      data-native={original}
    >
      {(!original || !ready || failed) && (
        <ProductImage
          image={
            original && desktop
              ? {
                  ...desktop,
                  src: failed ? MOBILE : SOURCE,
                  width: failed ? 5376 : 1792,
                  height: failed ? 3411 : 1137,
                }
              : image
          }
          mobileImage={original ? undefined : mobileImage}
          priority
          sizes="(max-width: 639px) 142.53vw, (max-width: 992px) 620px, (max-width: 1023px) 32.3vw, 93.333vw"
        />
      )}
      {ready && (
        <div
          className={styles.heroVector}
          role={image?.alt ? "img" : undefined}
          aria-label={image?.alt}
          dangerouslySetInnerHTML={vectorMarkup}
        />
      )}
    </div>
  );
}
